import React, { useState } from "react";
import type { JSX } from "react";
import {
  Button,
  CastingCardSkeleton,
  CastingFiltersSkeleton,
  CastingList,
  Modal,
  NavbarContainer,
  PublicPageGuideModal,
  SkeletonStatus,
} from "@/components";
import "@/components/organisms/Castings/CastingList.scss";
import { useCastings, usePublicPageGuide } from "@/hooks";
import {
  CASTINGS_PAGE_GUIDE_ACTION_TRACK_SUFFIX,
  CASTINGS_PAGE_GUIDE_CONFIRM_LABEL,
  CASTINGS_PAGE_GUIDE_INTRO,
  CASTINGS_PAGE_GUIDE_STEPS,
  CASTINGS_PAGE_GUIDE_STORAGE_KEY,
  CASTINGS_PAGE_GUIDE_TITLE,
  CASTINGS_PAGE_LEDE,
} from "@/data/public/castingsPage";
import "./Castings.scss";
import type { Casting } from "@/types";
import { copyToClipboard, generateWhatsAppLink } from "@/utils";
import {
  SiInstagram as Instagram,
  SiWhatsapp as Whatsapp,
} from "@icons-pack/react-simple-icons";
import { Mail, SquareArrowOutUpRight } from "lucide-react";

/**
 * Page showing available casting calls
 * @component
 * @returns {JSX.Element} Casting page component
 */
const Castings: React.FC = (): JSX.Element => {
  const { data: castings, loading, error } = useCastings();
  const { isGuideOpen, closeGuideForSession, dismissGuidePermanently } =
    usePublicPageGuide(CASTINGS_PAGE_GUIDE_STORAGE_KEY, !loading);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCasting, setSelectedCasting] = useState<Casting | null>(null);

  const handleOpenModal = (casting: Casting) => {
    setSelectedCasting(casting);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedCasting(null);
  };

  const buildCastingMessage = (castingTitle: string | undefined) => {
    return `Hola, ví en la página de Tercer Espacio que tienen un casting abierto para ${castingTitle}! Me gustaría postularme`;
  };

  const applicationButtonsMap = [
    {
      conditional: selectedCasting?.contact.email,
      icon: Mail,
      actionTrackSufix: "casting__email-apply",
      href: selectedCasting?.contact.email
        ? `mailto:${selectedCasting.contact.email!}?subject=${encodeURIComponent(`Casting para ${selectedCasting.title!}`)}&body=${encodeURIComponent(buildCastingMessage(selectedCasting.title!))}`
        : "#",
    },
    {
      conditional: selectedCasting?.contact.phone,
      icon: Whatsapp,
      actionTrackSufix: "casting__whatsapp-apply",
      href: selectedCasting?.contact.phone
        ? generateWhatsAppLink(
            selectedCasting.contact.phone!,
            buildCastingMessage(selectedCasting.title!),
          )
        : "",
    },
    {
      conditional: selectedCasting?.contact.instagram,
      icon: Instagram,
      actionTrackSufix: "casting__instagram-apply",
      href: `https://instagram.com/${selectedCasting?.contact.instagram}`,
    },
    {
      conditional: selectedCasting?.contact.externalLink,
      icon: SquareArrowOutUpRight,
      actionTrackSufix: "casting__external-link-apply",
      href: selectedCasting?.contact.externalLink,
    },
  ];

  return (
    <NavbarContainer title="Castings">
      {error && <p>Error al cargar castings: {error.message}</p>}
      {loading ? (
        <SkeletonStatus label="Cargando castings..." className="castings">
          <CastingFiltersSkeleton />
          <div className="castings__container">
            {Array.from({ length: 4 }).map((_, index) => (
              <CastingCardSkeleton key={`casting-skeleton-${index}`} />
            ))}
          </div>
        </SkeletonStatus>
      ) : (
        <>
          <p className="castings-page__lede">{CASTINGS_PAGE_LEDE}</p>
          <CastingList castings={castings} onTriggerCasting={handleOpenModal} />
          <Modal
            isOpen={isModalOpen}
            onClose={handleCloseModal}
            title="Aplicá a este casting"
          >
            {selectedCasting && (
              <div className="casting-modal-content">
                <div className="casting-modal-content__accent">
                  <p className="casting-modal-content__title">
                    <b>{selectedCasting.title}</b>
                  </p>
                  <p>{selectedCasting.description}</p>
                  {selectedCasting.fisic && (
                    <>
                      <p className="casting-modal-content__subtitle">
                        <b>Fisic to Rol:</b>
                      </p>
                      <p>{selectedCasting.fisic}</p>
                    </>
                  )}
                  <p className="casting-modal-content__subtitle">
                    <b>Para aplicar, tenés que enviar:</b>
                  </p>
                  {selectedCasting.requiredInfo
                    .split(",")
                    .map((info, index) => (
                      <p key={`required-info-${index}`}>
                        {"- "}
                        <span className="casting-modal-content__required-info-item">
                          {info.trim()}
                        </span>
                      </p>
                    ))}
                  <p className="casting-modal-content__subtitle">
                    <b>Contacto:</b>
                  </p>
                  {Object.entries(selectedCasting.contact).map(
                    ([key, value]) =>
                      value && (
                        <p
                          key={`contact-item-${key}`}
                          onClick={() => copyToClipboard(value)}
                          className="casting-modal-content__copy-contact"
                        >
                          {key === "instagram" ? `@${value}` : value}
                        </p>
                      ),
                  )}
                </div>
                <p>O podés elegir un medio para continuar:</p>
                <div className="casting-modal-content__buttons">
                  {applicationButtonsMap
                    .filter((btn) => btn.conditional)
                    .map(({ href, actionTrackSufix, icon: Icon }) => (
                      <Button
                        key={`application-btn_${actionTrackSufix}`}
                        href={href}
                        actionTrackSufix={actionTrackSufix}
                        variant="primary"
                        className="casting-modal-content__button"
                        motionScale="base"
                      >
                        <Icon />
                      </Button>
                    ))}
                </div>
              </div>
            )}
          </Modal>
        </>
      )}
      <PublicPageGuideModal
        isOpen={isGuideOpen}
        onClose={closeGuideForSession}
        onConfirm={dismissGuidePermanently}
        title={CASTINGS_PAGE_GUIDE_TITLE}
        intro={CASTINGS_PAGE_GUIDE_INTRO}
        steps={CASTINGS_PAGE_GUIDE_STEPS}
        confirmLabel={CASTINGS_PAGE_GUIDE_CONFIRM_LABEL}
        actionTrackSufix={CASTINGS_PAGE_GUIDE_ACTION_TRACK_SUFFIX}
      />
    </NavbarContainer>
  );
};

export default Castings;
